/**
 * The section a screen is built out of, promoted from seven copies.
 *
 * ## Why this is shared rather than per chapter
 *
 * `docs/adr/9006` is the record. The same move `docs/adr/0015` made for `AsyncRegion` and
 * `docs/adr/0083` made for `FixtureProvenance`, for the third time and for the same
 * reason. Measured on
 * 2026-09-24, `Panel` existed in seven files and no two agreed:
 *
 * | file | id prefix | radius | how the section was named |
 * |---|---|---|---|
 * | `features/preseason/panels.tsx` | `preseason-panel-` | `rounded` | `aria-labelledby` → the `<h2>` |
 * | `features/launch/panels.tsx` | `launch-panel-` | `rounded` | `aria-labelledby` → the `<h2>` |
 * | `features/inseason/panels.tsx` | **`launch-panel-`** | `rounded` | `aria-labelledby` → the `<h2>` |
 * | `features/exit/panels.tsx` | `exit-panel-` | `rounded` | `aria-labelledby` → the `<h2>` |
 * | `features/postseason/panels.tsx` | `postseason-panel-` | `rounded` | `aria-labelledby` → the `<h2>` |
 * | `features/foundations/panels.tsx` | `panel-` | `rounded` | `aria-labelledby` → the `<h2>` |
 * | `app/routes/overview/panels.tsx` | — | `rounded-control` | `aria-label`, **plus** an unlabelled `<h2>` |
 *
 * Two of those rows are defects rather than drift. `inseason` carries **`launch-panel-`**,
 * a copy-paste that shipped: every in-season panel names itself with another chapter's
 * prefix, and two panels sharing a `titleKey` across the two chapters would collide on a
 * document id. And the overview names the section twice — `aria-label` on the `<section>`
 * and an `<h2>` inside it — so a screen reader announces the heading, enters the region and
 * is told the same words again.
 *
 * Neither would have been found by review. They were found by putting the seven side by
 * side, which is the argument for one implementation and not seven.
 *
 * ## The id comes from `useId`, not from the title
 *
 * `panel-${titleKey}` is unique only while no screen mounts the same panel twice, which is
 * a property of today's screens rather than of the component. `useId` is unique by
 * construction and survives a screen that renders two comparison panels off one key.
 *
 * ## The two variants, and why `cell` exists
 *
 * `raised` is what every one of the seven rendered: a bordered, rounded card on
 * `surface-raised`. It is still the default, so nothing changed shape when this landed.
 *
 * `cell` is the composition the redesign asks for — no border, no radius, the page's own
 * ground — and it exists so `CellGrid` can butt sections against each other separated by a
 * single hairline instead of by a 16px gutter between two rounded cards. A card inside a
 * card is the "gestionale" reading the redesign is trying to lose, and it is caused by a
 * panel that insists on its own border wherever it is mounted. See `./band.tsx`.
 *
 * ## One line under the title, the rest behind (i) — audit v5b D-01
 *
 * Every section used to open on a paragraph of two to four lines saying why it was built the
 * way it was, and often closed on another. Read once, that is useful; read every morning it is
 * noise, and the page reads as a document rather than as a tool. `subtitleKey` is the one line
 * a planner reads; `aboutKey` is the rest, behind an (i) beside the title — still translated,
 * still reachable by keyboard and screen reader, no longer in the way of the numbers.
 *
 * ## Provenance in the header — ADR 0083 as amended (audit v5b D-06)
 *
 * A `FixtureProvenance` anywhere inside registers its reason here; the panel draws ONE
 * "Illustrative" tag beside its title and an amber rule down its body. See `./provenance.tsx`.
 */

import { useCallback, useId, useState } from 'react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AboutPopover } from '../primitives/about-popover';
import { Enclosure, useEnclosed } from '../primitives/enclosure';
import { IllustrativeProvider, IllustrativeTag } from './provenance';

export interface PanelProps {
  /** ICU catalogue key. There is deliberately no `title: string` (G6). */
  readonly titleKey: string;
  /**
   * The line under the title: what the panel is measured over, or what it refuses to
   * answer. Optional, because a panel whose title is already the whole sentence should not
   * be forced to carry a second, emptier one.
   */
  readonly subtitleKey?: string;
  /**
   * What the panel is for and why it is built this way — the paragraphs that used to open and
   * close the section (audit v5b D-01). Rendered behind an (i) beside the title, one
   * paragraph per key, in order.
   */
  readonly aboutKey?: string | readonly string[];
  /** Values for an ICU interpolation in any of the keys. */
  readonly values?: Readonly<Record<string, string | number>>;
  /**
   * The affordance on the title's own line — "view all 12", an export, a toggle. It sits
   * in the header rather than at the foot so a long panel does not hide its own action
   * below the fold.
   */
  readonly action?: ReactNode;
  /**
   * `band` (the default) — a full-width section of the page, closed by a hairline. It was a
   * `raised` card — white, bordered, rounded, 16px from its neighbours — until audit v4 G-16:
   * a page of stacked cards is the "gestionale" reading the redesign set out to lose, and the
   * mockup separates sections by a rule, not by a gap between boxes. `cell` is a band's
   * contents inside a `CellGrid` cell.
   */
  readonly variant?: 'band' | 'cell';
  /**
   * `half` — a short section (an empty state, a couple of figures) that may sit beside the next
   * short one instead of leaving the right half of the page empty (audit v5b D-05). It takes
   * effect inside `Sections`, which puts two consecutive `half` panels side by side from the `lg`
   * breakpoint (1200px) up; anywhere else, and for a `half` with no `half` neighbour, the panel is
   * full width as before.
   */
  readonly span?: 'full' | 'half';
  /**
   * The guided tour's anchor for this panel (`data-tour`, ADR 9044) — set by a screen whose tour
   * has a step about it. A literal at the call site, so `tools/doc-sections.mjs` can find it.
   */
  readonly tourAnchor?: string;
  readonly children: ReactNode;
}

export function Panel({
  titleKey, subtitleKey, aboutKey, values, action, variant = 'band', span = 'full', tourAnchor, children,
}: PanelProps): React.JSX.Element {
  const { t } = useTranslation();
  const headingId = useId();
  // A panel INSIDE another is a subsection of that band: no rule of its own, or a band would
  // be cut into ruled strips by its own contents.
  const nested = useEnclosed();
  const about = aboutKey === undefined ? [] : typeof aboutKey === 'string' ? [aboutKey] : aboutKey;
  // The provenance reasons the `FixtureProvenance`s inside this panel have registered.
  const [illustrative, setIllustrative] = useState<readonly string[]>([]);
  const register = useCallback((reasonKey: string) => {
    setIllustrative((current) => (current.includes(reasonKey) ? current : [...current, reasonKey]));
    return () => setIllustrative((current) => current.filter((key) => key !== reasonKey));
  }, []);
  const title = t(titleKey, values ?? {});
  return (
    <section
      aria-labelledby={headingId}
      data-span={span}
      // A block of the page grid (brief part 3 §B6, ADR 9038).
      data-layout-block=""
      data-tour={tourAnchor}
      className={[
        'flex min-w-0 flex-col gap-3',
        variant === 'cell'
          ? 'bg-surface-page p-6'
          : nested
            ? ''
            // The rule closes the band — every band, including the last. `last:border-b-0`
            // was tried and removed: it reads the band's place in its PARENT, so of two bands
            // side by side in a grid the first was ruled and the second was not.
            : 'border-hairline border-b pb-6',
      ].join(' ')}
    >
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <div className="flex min-w-0 flex-col gap-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 id={headingId} className="text-section text-ink-primary font-display uppercase">
              {title}
            </h2>
            {illustrative.length > 0 && <IllustrativeTag reasonKeys={illustrative} />}
            {about.length > 0 && <AboutPopover aboutKeys={about} title={title} {...(values === undefined ? {} : { values })} />}
          </div>
          {subtitleKey !== undefined && (
            <p className="text-caption text-ink-secondary max-w-prose">
              {t(subtitleKey, values ?? {})}
            </p>
          )}
        </div>
        {action}
      </div>
      {/* Everything below the heading is INSIDE something that has named itself, and the
          designed states read that rather than assuming it — see `primitives/enclosure.tsx`. An
          `AsyncRegion` here renders its empty and error surfaces `inline`; the same
          component mounted at screen level, wrapping panels rather than sitting in one,
          renders them standalone.

          The amber rule down the body is D-06's frame: while any part of this panel is
          illustrative, the data it holds is marked as such along its whole height — the tag
          says it once, the rule keeps saying it as the planner reads down. */}
      <IllustrativeProvider value={register}>
        <div
          className={[
            'flex min-w-0 flex-col gap-3',
            illustrative.length > 0 ? 'border-status-warning border-s-2 ps-4' : '',
          ].join(' ')}
        >
          <Enclosure>{children}</Enclosure>
        </div>
      </IllustrativeProvider>
    </section>
  );
}

/**
 * The (i) beside a section's title and the paragraphs behind it (audit v5b D-01). `Panel` and
 * `ScopeChooser` (ADR 9036) both draw it — and since DEC-111 so does every column, KPI and chart,
 * which is why it now lives among the primitives (`../primitives/about-popover`, ADR 9047).
 */
export { AboutPopover, type AboutPopoverProps } from '../primitives/about-popover';
